import React, { useEffect, useState, useRef } from "react";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import { AiFillDelete } from "react-icons/ai";
import { FaUpload, FaImage } from "react-icons/fa";
import { BarLoader } from "react-spinners";
import {
  AddSlider,
  DeleteSliders,
  FetchSliders,
} from "../../Redux/Slices/Slider";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { resolveStorageUrl } from "../../Helper/CoreHelper";
import "../../asset/new/css/admin-modern.css";

const Sliders = () => {
  const [image, setImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { slidersData } = useSelector((state) => state?.Slider);

  //Submit
  const handleFileChange = (e) => {
    setImage(e.target.files[0]);
  };
  
  // Handle image upload
  const handleImageUpload = async () => {
    if (image) {
      try {
        setIsUploading(true);
        const formData = new FormData();
        formData.append("image", image);
        await AddSlider(formData, "multipartformdata");
        dispatch(FetchSliders());
        setImage(null);
        toast.success("Image uploaded successfully");
        
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        toast.error("Failed to upload image");
        console.error("Upload error:", error);
      } finally {
        setIsUploading(false);
      }
    } else {
      toast.error("Please select an image first");
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this slider image?")) return;
    try {
      await DeleteSliders(id);
      dispatch(FetchSliders());
      toast.success("Slider deleted successfully");
    } catch (error) {
      toast.error("Failed to delete slider");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(FetchSliders());
      setLoading(false);
    };
    fetchData();
    document.title = "Sliders | Admin";
  }, [dispatch]);

  // Format the date in Indian time zone
  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const options = {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    };
    const formattedDate = new Intl.DateTimeFormat("en-IN", options).format(
      dateObject
    );
    return formattedDate;
  };

  const validSliderItems = Array.isArray(slidersData)
    ? slidersData
        .map((item) => ({ ...item, image: resolveStorageUrl(item?.image) }))
        .filter((item) => item.image)
    : [];

  return (
    <AdminLayout
      title="Home Sliders"
      subtitle="Manage homepage carousel images"
    >
      {/* Upload Card */}
      <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="admin-card-body">
          <h3 style={{ fontSize: "1.125rem", fontWeight: "600", marginBottom: 'var(--spacing-md)' }}>
            Upload New Slider Image
          </h3>
          <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "300px" }}>
              <label className="admin-form-label">Choose Image</label>
              <input
                ref={fileInputRef}
                onChange={handleFileChange}
                type="file"
                accept="image/*"
                className="admin-input"
                disabled={isUploading}
              />
              {image && (
                <p style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "var(--admin-text-secondary)" }}>
                  <FaImage className="me-2" />
                  {image.name}
                </p>
              )}
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={handleImageUpload}
              disabled={isUploading || !image}
              style={{ whiteSpace: "nowrap" }}
            >
              {isUploading ? (
                <>
                  <span className="me-2">Uploading</span>
                  <BarLoader color="#ffffff" height={5} width={50} />
                </>
              ) : (
                <>
                  <FaUpload className="me-2" />
                  Upload Image
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Preview</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSkeleton type="table-row" count={5} columns={4} />
                ) : validSliderItems.length > 0 ? (
                  validSliderItems.map((item, index) => (
                    <tr key={item._id}>
                      <td>
                        <span className="admin-badge admin-badge-light">
                          {index + 1}
                        </span>
                      </td>
                      <td>
                        <img
                          src={item.image}
                          alt={`Slider ${index + 1}`}
                          onError={(event) => {
                            event.currentTarget.style.visibility = "hidden";
                          }}
                          style={{
                            width: "80px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--admin-border-color)"
                          }}
                        />
                      </td>
                      <td>{formatDate(item?.created_at)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleDelete(item?._id)}
                          className="admin-btn admin-btn-sm admin-btn-danger"
                          disabled={isUploading}
                          title="Delete"
                        >
                          <AiFillDelete />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="admin-empty-state">
                        <FaImage className="fa-3x mb-3" style={{ fontSize: "3rem" }} />
                        <h4>No Slider Images</h4>
                        <p>Upload your first slider image to get started.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Sliders;